import React, { useState, useEffect } from "react";
import "../../styles/components/clients/SponsorCarousel.css";

/**
 * Inline carousel for displaying sponsors.
 * @param {Object} props
 * @param {string} props.clientName - Client name to display in header
 * @param {Array<Object>} props.sponsors - Array of sponsor objects with displayName
 * @param {string} props.selectedYear - Year label for display ("2024", "All Years", etc.)
 * @param {string} [props.error] - Error message to display if sponsor data is missing
 * @param {Function} [props.onSponsorIndexChange] - Callback when sponsor index changes
 */
const SponsorCarousel = ({ clientName, sponsors, selectedYear, error, onSponsorIndexChange }) => {
  const [index, setIndex] = useState(0);

  // Reset index when sponsors array LENGTH changes (not just reference)
  useEffect(() => {
    setIndex(0);
  }, [sponsors?.length]); // Only reset when length changes, not on every reference change

  // Notify parent when index changes
  const updateIndex = (newIndex) => {
    setIndex(newIndex);
    if (onSponsorIndexChange) {
      onSponsorIndexChange(clientName, newIndex);
    }
  };

  if (error) {
    return (
      <div className="sponsor-carousel-error">
        {error}
      </div>
    );
  }

  if (!sponsors || sponsors.length === 0) {
    return (
      <div className="sponsor-carousel-container">
        <div className="sponsor-carousel-empty">
          n/a
        </div>
      </div>
    );
  }

  const handlePrev = (e) => {
    e.stopPropagation();
    const newIndex = index === 0 ? sponsors.length - 1 : index - 1;
    updateIndex(newIndex);
  };

  const handleNext = (e) => {
    e.stopPropagation();
    const newIndex = index === sponsors.length - 1 ? 0 : index + 1;
    updateIndex(newIndex);
  };

  const total = sponsors.length;
  // Ensure index is valid
  const safeIndex = Math.min(index, total - 1);
  const current = safeIndex + 1;
  const yearLabel = selectedYear || 'All Years';
  const currentSponsor = sponsors[safeIndex];

  return (
    <div className="sponsor-carousel-container">
      <div className="sponsor-carousel">
      <button
        className="sponsor-carousel-arrow"
        onClick={handlePrev}
        aria-label="Previous sponsor"
        tabIndex={0}
        type="button"
      >
        {/* Left chevron SVG, rounded style */}
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
          <path d="M20 8L12 16L20 24" stroke="black" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
        <div className="sponsor-carousel-info">
          <span className="sponsor-carousel-label">
            Sponsors ({current}/{total}) - {yearLabel}
          </span>
          <span
            className="sponsor-carousel-name"
            data-tooltip={currentSponsor?.displayName}
            title={currentSponsor?.displayName}
          >
            {currentSponsor?.displayName}
          </span>
        </div>
      <button
        className="sponsor-carousel-arrow"
        onClick={handleNext}
        aria-label="Next sponsor"
        tabIndex={0}
        type="button"
      >
        {/* Right chevron SVG, rounded style */}
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
          <path d="M12 8L20 16L12 24" stroke="black" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      </div>
    </div>
  );
};

export default SponsorCarousel;
